import { HoaCreate } from '../../clients/rust-api-client';
import { stripPhoneFormatting } from '../../utils/phoneUtils';
import { needsTimezoneChoice } from '../timezones';

/**
 * The create-an-HOA modal's draft, and the pure rules over it (#665):
 * what is still missing, what is filled in but wrong, and the request a
 * complete draft becomes. Kept out of the component so they are tested
 * without rendering it.
 */
export interface HoaDraft {
  companyId: string | null;
  name: string;
  description: string;
  street: string;
  street2: string;
  city: string;
  state: string | null;
  /** An IANA zone, or null for the state's default (#599). */
  timezone: string | null;
  zip: string;
  phone: string;
  email: string;
  website: string;
}

export const EMPTY_HOA_DRAFT: HoaDraft = {
  companyId: null,
  name: '',
  description: '',
  street: '',
  street2: '',
  city: '',
  state: null,
  timezone: null,
  zip: '',
  phone: '',
  email: '',
  website: '',
};

// The API's limits (`HoaCreate` in back-end-rs/src/api/v1/hoa/models.rs).
export const NAME_MAX = 225;
export const DESCRIPTION_MAX = 1000;
export const STREET_MAX = 255;
export const CITY_MAX = 255;
export const EMAIL_MAX = 100;
export const WEBSITE_MAX = 225;

/** Required and empty, as "Still needs …" phrases. */
export const missingForHoa = (d: HoaDraft): string[] =>
  [
    !d.companyId && 'a company',
    !d.name.trim() && 'a name',
    !d.street.trim() && 'a street',
    !d.city.trim() && 'a city',
    !d.state && 'a state',
    // A state spanning two zones can't answer this itself, and its
    // default is usually the eastern zone, which charges early in the
    // western part. So onboarding asks rather than guesses (#599).
    needsTimezoneChoice(d.state) && !d.timezone && 'a timezone',
    !d.zip.trim() && 'a ZIP code',
    !stripPhoneFormatting(d.phone) && 'a contact phone',
    !d.email.trim() && 'a contact email',
  ].filter(Boolean) as string[];

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const ZIP = /^\d{5}(-\d{4})?$/;

const isUrl = (value: string): boolean => {
  try {
    const url = new URL(value);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
};

/**
 * Fields that are filled in but wrong, keyed by field. An empty field is
 * never an error here; that is `missingForHoa`'s job, so a fresh form
 * isn't covered in red.
 */
export const hoaFieldErrors = (
  d: HoaDraft
): Partial<Record<keyof HoaDraft, string>> => {
  const errors: Partial<Record<keyof HoaDraft, string>> = {};
  const zip = d.zip.trim();
  if (zip && !ZIP.test(zip)) errors.zip = 'Use 12345 or 12345-6789';
  const phone = stripPhoneFormatting(d.phone);
  if (phone && !/^\d{10,11}$/.test(phone))
    errors.phone = 'Phone numbers have 10 or 11 digits';
  const email = d.email.trim();
  if (email && (!EMAIL.test(email) || email.length > EMAIL_MAX))
    errors.email = 'Not an email address';
  const website = d.website.trim();
  if (website && (!isUrl(website) || website.length > WEBSITE_MAX))
    errors.website = 'Use a full address, like https://example.com';
  if (d.name.trim().length > NAME_MAX)
    errors.name = `At most ${NAME_MAX} characters`;
  if (d.description.trim().length > DESCRIPTION_MAX)
    errors.description = `At most ${DESCRIPTION_MAX} characters`;
  if (d.street.trim().length > STREET_MAX || d.street2.trim().length > STREET_MAX)
    errors.street = `At most ${STREET_MAX} characters`;
  if (d.city.trim().length > CITY_MAX)
    errors.city = `At most ${CITY_MAX} characters`;
  return errors;
};

/**
 * The request for a complete, valid draft, or null. The phone goes as
 * digits only, as the old dialog sent it; empty optional fields go as
 * null, not as empty strings. No timezone means "the state's default",
 * which the API reads from the field being absent.
 */
export const buildHoaCreate = (d: HoaDraft): HoaCreate | null => {
  if (missingForHoa(d).length || !d.companyId || !d.state) return null;
  if (Object.keys(hoaFieldErrors(d)).length) return null;
  const text = (v: string) => (v.trim() ? v.trim() : null);
  return {
    company_id: d.companyId,
    name: d.name.trim(),
    description: text(d.description),
    street: d.street.trim(),
    street2: text(d.street2),
    city: d.city.trim(),
    state: d.state,
    zip: d.zip.trim(),
    country: 'USA',
    contact_phone: stripPhoneFormatting(d.phone),
    contact_email: d.email.trim(),
    website_url: text(d.website),
    // Left out, the API derives the zone from the state.
    ...(d.timezone ? { timezone: d.timezone } : {}),
  };
};
